import React, {useEffect, useState} from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {useNavigate} from "react-router-dom";
import {requireAuth} from "../../shared/services/auth.service";
import {enqueueSnackbar} from "notistack";
import {logError} from "../../shared/services/logger.service";
import {GridColDef, GridRowParams} from '@mui/x-data-grid';
import {useToolbar} from "../../components/ToolbarContext";
import {UrlFilterAwareDataGrid} from "../../shared/components/UrlFilterAwareDataGrid";
import {CustomOperationMethodDto} from "./dtos/operation-method.dto";
import {getAllOperations} from "./services/operation-method.service";

function OperationMethodListPage() {
  const navigate = useNavigate();
  const toolbarContext = useToolbar();
  const [allOperations, setAllOperations] = useState<CustomOperationMethodDto[]>([]);
  const [operationsIsLoading, setOperationsIsLoading] = useState(false);

  useEffect(() => {
    toolbarContext.initToolbar('OperationMethodListPage', 'Operation Methods');
    requireAuth(navigate)
      .then(() => fetchAllOperations())
      .catch(e => {
        logError('Error requiring auth', {}, e)
        enqueueSnackbar(`Error requiring auth: ${e}`, {variant: 'error', autoHideDuration: 5000});
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchAllOperations() {
    setOperationsIsLoading(true);
    await getAllOperations(navigate)
      .then(operations => setAllOperations(operations))
      .catch(e => {
        logError('Error fetching operations', {}, e)
        enqueueSnackbar(`Error fetching operations: ${e}`, {variant: 'error', autoHideDuration: 5000});
      })
      .finally(() => {
        setOperationsIsLoading(false)
      });
  }

  const operationColumns: GridColDef[] = [
    { field: 'id', headerName: 'Id', width: 100 },
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'behavior', headerName: 'Behavior', width: 200 },
    { field: 'createdAt', headerName: 'Created At', width: 200 },
  ];

  const operationRowBuilder = (operation: CustomOperationMethodDto) => {
    return {
      id: operation.id,
      name: operation.name,
      behavior: operation.behavior,
      createdAt: operation.createdAt
    }
  };

  const handleAddNewOperationClick = () => {
    navigate('/equipment/operation-methods/new');
  }

  const handleRowClick = (params: GridRowParams) => {
    navigate(`/equipment/operation-methods/${params.id}`);
  };

  return (
    <div style={{height: '100%'}}>
      <Paper sx={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%', padding: 3}}>
        {/* The bar no longer shows a title (SQ-554), so the page names itself, like the detail page. */}
        <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2, alignSelf: 'flex-start'}}>
          <IconButton onClick={() => navigate('/equipment')} size="small" aria-label="Back to Equipment & Tools">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="h1">Operation Methods</Typography>
        </Box>
        <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', paddingBottom: 2}}>
          <Button variant="contained" onClick={handleAddNewOperationClick}>Add New Operation Method</Button>
        </Box>
        <UrlFilterAwareDataGrid
          columns={operationColumns}
          rowBuilder={operationRowBuilder}
          data={allOperations}
          onRowClick={handleRowClick}
          loading={operationsIsLoading}
          defaultSortModel={[{ field: 'createdAt', sort: 'desc' }]}
        />
      </Paper>
    </div>
  );
}

export default OperationMethodListPage;
